/**
 * Module dependencies.
 */

import React from 'react';
import ReactJson from 'react-json-view';
import qs from 'qs';

/**
 * `LogBodyRendererFlatProps` type.
 */

type LogBodyRendererFlatProps = {
  body: any;
  type: string;
};

/**
 * Parse body if it's a string.
 */

function parseBody(body: any): any {
  try {
    return typeof body === 'string' ? JSON.parse(body) : body;
  } catch (error) {
    return null;
  }
}

/**
 * Render URL with query params displayed inline.
 */

function renderUrl(url: string): JSX.Element {
  const urlParts = url.split('?');
  const baseUrl = urlParts[0];
  const queryString = urlParts[1];

  if (!queryString) {
    return <span style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>{url}</span>;
  }

  const params = qs.parse(queryString);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>{baseUrl}</span>
      <div style={{ paddingLeft: '16px' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#666' }}>
          {'Query Parameters:'}
        </div>
        <pre style={{ margin: 0, fontSize: '12px', color: '#000000' }}>{JSON.stringify(params, null, 2)}</pre>
      </div>
    </div>
  );
}

/**
 * Render task/verbose type - show messages array.
 */

function renderTaskVerbose(parsedBody: any) {
  const messages = parsedBody?.messages;

  if (!messages || !Array.isArray(messages)) {
    return renderDefault(parsedBody);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {messages.map((message: any, index: number) => (
        <div
          key={index}
          style={{
            padding: '8px 12px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
            borderLeft: '3px solid #1890ff',
            fontSize: '13px',
            color: '#000000'
          }}
        >
          {typeof message === 'string' ? message : JSON.stringify(message, null, 2)}
        </div>
      ))}
    </div>
  );
}

/**
 * Render info type - show messages array flat.
 */

function renderInfo(parsedBody: any) {
  const messages = parsedBody?.messages;

  // If messages is an array, render each item
  if (Array.isArray(messages)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {messages.map((item: any, index: number) => {
          // If item is a string, show it directly
          if (typeof item === 'string') {
            return (
              <div key={index} style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>
                {item}
              </div>
            );
          }

          // If item is an object, show it expanded
          return (
            <div key={index} style={{ paddingLeft: '16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#666' }}>
                {'Object '}
                {index + 1}
                {':'}
              </div>
              <pre style={{ margin: 0, fontSize: '12px', color: '#000000' }}>{JSON.stringify(item, null, 2)}</pre>
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback: check for single message property
  const message = parsedBody?.message;

  if (!message) {
    return renderDefault(parsedBody);
  }

  // If message is a string, show it directly
  if (typeof message === 'string') {
    return <div style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>{message}</div>;
  }

  // If message is an object, show it expanded
  return (
    <div>
      <pre style={{ margin: 0, fontSize: '12px', color: '#000000' }}>{JSON.stringify(message, null, 2)}</pre>
    </div>
  );
}

/**
 * Render request type - just return null since details are shown in top-level fields.
 */

function renderRequest() {
  // Request details are shown in the top-level fields (Method, URL, Headers)
  // so we don't need to show anything in the Body section
  return null;
}

/**
 * Render response type - show method, status code, URL, and response details.
 */

function renderResponse(parsedBody: any) {
  const context = parsedBody?.context;
  const messages = parsedBody?.messages;
  const firstMessage = messages && messages.length > 0 ? messages[0] : null;

  if (!context || !context.url || !context.method || context.statusCode === undefined) {
    return renderDefault(parsedBody);
  }

  const methodColors: Record<string, string> = {
    GET: '#52c41a',
    POST: '#1890ff',
    PUT: '#faad14',
    PATCH: '#13c2c2',
    DELETE: '#f5222d'
  };

  // Status code colors based on HTTP status ranges
  const getStatusColor = (statusCode: number): string => {
    if (statusCode >= 200 && statusCode < 300) return '#52c41a'; // Success - green
    if (statusCode >= 300 && statusCode < 400) return '#1890ff'; // Redirect - blue
    if (statusCode >= 400 && statusCode < 500) return '#faad14'; // Client error - orange
    if (statusCode >= 500) return '#f5222d'; // Server error - red
    return '#d9d9d9'; // Unknown - gray
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Method, Status Code and URL */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: methodColors[context.method] || '#d9d9d9'
            }}
          >
            {context.method}
          </span>
          <span
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#fff',
              backgroundColor: getStatusColor(context.statusCode)
            }}
          >
            {context.statusCode}
          </span>
        </div>
        {renderUrl(context.url)}
      </div>

      {/* Response Details from messages */}
      {firstMessage && (
        <>
          {/* Duration */}
          {firstMessage.duration !== undefined && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>{'Duration:'}</span>
              <span style={{ fontSize: '13px', color: '#000000', fontFamily: 'monospace' }}>
                {firstMessage.duration}
                {'ms'}
              </span>
            </div>
          )}

          {/* Response Headers */}
          {firstMessage.responseHeaders && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#666' }}>
                {'Response Headers:'}
              </div>
              <div style={{ paddingLeft: '16px' }}>
                <ReactJson
                  collapsed={1}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  name={false}
                  src={firstMessage.responseHeaders}
                  theme={'monokai'}
                />
              </div>
            </div>
          )}

          {/* Response Body */}
          {firstMessage.responseBody !== undefined && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#666' }}>
                {'Response Body:'}
              </div>
              <div style={{ paddingLeft: '16px' }}>
                <ReactJson
                  collapsed={1}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  enableClipboard={true}
                  name={false}
                  src={firstMessage.responseBody}
                  theme={'monokai'}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Render error type - show context (method + URL) and messages.
 */

function renderError(parsedBody: any) {
  const context = parsedBody?.context;
  const messages = parsedBody?.messages;

  const methodColors: Record<string, string> = {
    GET: '#52c41a',
    POST: '#1890ff',
    PUT: '#faad14',
    PATCH: '#13c2c2',
    DELETE: '#f5222d'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Context: Method + URL */}
      {context && context.method && context.url && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: methodColors[context.method] || '#d9d9d9'
              }}
            >
              {context.method}
            </span>
          </div>
          {renderUrl(context.url)}
        </div>
      )}

      {/* Messages */}
      {messages && Array.isArray(messages) && messages.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>{'Error Messages:'}</div>
          {messages.map((message: any, index: number) => (
            <div
              key={index}
              style={{
                padding: '8px 12px',
                background: '#fff1f0',
                borderRadius: '4px',
                borderLeft: '3px solid #f5222d'
              }}
            >
              {typeof message === 'string' ? (
                <span style={{ fontSize: '13px', color: '#000000', lineHeight: '1.6' }}>{message}</span>
              ) : (
                <div>
                  {message.errorType && (
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#f5222d', marginBottom: '4px' }}>
                      {message.errorType}
                    </div>
                  )}
                  {message.message && (
                    <div style={{ fontSize: '13px', color: '#000000', marginBottom: '8px' }}>{message.message}</div>
                  )}
                  <pre style={{ margin: 0, fontSize: '11px', color: '#666' }}>{JSON.stringify(message, null, 2)}</pre>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* If no messages or context, show default JSON */}
      {!messages && !context && renderDefault(parsedBody)}
    </div>
  );
}

/**
 * Render default - show JSON viewer fully expanded.
 */

function renderDefault(parsedBody: any) {
  return (
    <ReactJson
      collapsed={1}
      displayDataTypes={false}
      displayObjectSize={false}
      enableClipboard={true}
      name={false}
      src={parsedBody}
      theme={'monokai'}
    />
  );
}

/**
 * Export `LogBodyRendererFlat` component.
 */

export function LogBodyRendererFlat({ body, type }: LogBodyRendererFlatProps): JSX.Element {
  const parsedBody = parseBody(body);

  if (!parsedBody) {
    return <span style={{ color: '#999' }}>{'Invalid JSON'}</span>;
  }

  // Route to specific renderer based on type
  switch (type.toLowerCase()) {
    case 'task':
    case 'verbose':
      return <>{renderTaskVerbose(parsedBody)}</>;

    case 'info':
      return <>{renderInfo(parsedBody)}</>;

    case 'request':
      return <>{renderRequest()}</>;

    case 'response':
      return <>{renderResponse(parsedBody)}</>;

    case 'error':
      return <>{renderError(parsedBody)}</>;

    default:
      return <>{renderDefault(parsedBody)}</>;
  }
}
